import {
  iframeGenerator,
  recoverEventListeners,
  recoverDocumentListeners,
  insertScriptToIframe,
  patchEventTimeStamp,
  patchDegradeInstanceofAcrossRealms,
} from "./iframe";
import { syncUrlToWindow, syncUrlToIframe, clearInactiveAppUrl } from "./sync";
import {
  createWujieWebComponent,
  clearChild,
  getPatchStyleElements,
  renderElementToContainer,
  renderTemplateToShadowRoot,
  renderTemplateToIframe,
  initRenderIframeAndContainer,
  removeLoading,
} from "./shadow";
import { proxyGenerator, localGenerator } from "./proxy";
import { ScriptResultList } from "./entry";
import { getPlugins, getPresetLoaders } from "./plugin";
import { removeEventListener } from "./effect";
import {
  SandboxCache,
  idToSandboxCacheMap,
  addSandboxCacheWithWujie,
  deleteWujieById,
  rawElementAppendChild,
  rawDocumentQuerySelector,
} from "./common";
import { EventBus, appEventObjMap, EventObj } from "./event";
import { EventCleanupTracker } from "./tracker";
import { isFunction, wujieSupport, appRouteParse, requestIdleCallback, getAbsolutePath, eventTrigger } from "./utils";
import { WUJIE_DATA_ATTACH_CSS_FLAG, WUJIE_APP_ID, WUJIE_FONT_STYLE_CONTAINER_ATTR } from "./constant";
import { plugin, ScriptObjectLoader, loadErrorHandler } from "./index";

export type lifecycle = (appWindow: Window) => any;
type lifecycles = {
  beforeLoad: lifecycle;
  beforeMount: lifecycle;
  afterMount: lifecycle;
  beforeUnmount: lifecycle;
  afterUnmount: lifecycle;
  activated: lifecycle;
  deactivated: lifecycle;
  loadError: loadErrorHandler;
};
/**
 * 基于 Proxy和iframe 实现的沙箱
 */
export default class Wujie {
  public id: string;
  /** 激活时路由地址 */
  public url: string;
  /** 子应用保活 */
  public alive: boolean;
  /** window代理 */
  public proxy: WindowProxy;
  /** document代理 */
  public proxyDocument: Object;
  /** location代理 */
  public proxyLocation: Object;
  /**
   * 释放 window / document / location 代理。
   * 代理的 handler 闭包捕获了 iframe / urlElement 等 DOM 引用，destroy 时调用此函数
   * 解除代理与 handler 的关联，斩断「主应用 → 代理闭包 → iframe」的引用链。
   */
  public proxyRevoke: () => void;
  /** 事件中心 */
  public bus: EventBus;
  /** 容器 */
  public el: HTMLElement;
  /** js沙箱 */
  public iframe: HTMLIFrameElement;
  /** css沙箱 */
  public shadowRoot: ShadowRoot;
  /** 子应用的template */
  public template: string;
  /** 子应用代码替换钩子 */
  public replace: (code: string) => string;
  /** 子应用自定义fetch */
  public fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
  /** 子应用的生命周期 */
  public lifecycles: lifecycles;
  /** 子应用的插件 */
  public plugins: Array<plugin>;
  /** js沙箱ready态 */
  public iframeReady: Promise<void>;
  /** 子应用预加载态 */
  public preload: Promise<void>;
  /** 降级时渲染iframe的属性 */
  public degradeAttrs: { [key: string]: any };
  /** 子应用js执行队列 */
  public execQueue: Array<Function>;
  /** 子应用执行过标志 */
  public execFlag: boolean;
  /** 子应用激活标志 */
  public activeFlag: boolean;
  /** 子应用mount标志 */
  public mountFlag: boolean;
  /** 子应用销毁标志，防止 destroy 被并发重复执行 */
  public destroyed = false;
  /** 路由同步标志 */
  public sync: boolean;
  /** 子应用短路径替换，路由同步时生效 */
  public prefix: { [key: string]: string };
  /** 子应用跳转标志 */
  public hrefFlag: boolean;
  /** 子应用采用fiber模式执行 */
  public fiber: boolean;
  /** 子应用降级标志 */
  public degrade: boolean;
  /** 子应用降级document */
  public document: Document;
  /** 子应用styleSheet元素 */
  public styleSheetElements: Array<HTMLLinkElement | HTMLStyleElement>;

  /** 子应用 font-face 样式元素，挂载在最外层 document.head */
  public fontStyleSheetElements: Array<HTMLStyleElement> = [];
  /**
   * 子应用通过 document.head.appendChild(<script>) 触发的动态脚本节点。
   * 由 insertScriptToIframe 在收到 rawElement（即 effect.ts 转发的动态 script）
   * 时登记，sandbox.destroy() 时统一从 iframe head detach 并清空。
   */
  public dynamicScriptElements: Array<HTMLScriptElement> = [];
  /**
   * 动态 <link rel=stylesheet> 以空 href 插入（先 appendChild 后 setAttribute('href')，
   * 如 tinymce 的 StyleSheetLoader）时，effect.ts 会注册一个 MutationObserver 监听
   * href 的后续赋值。这些 observer 必须在 destroy 时统一 disconnect，否则游离 link
   * 会通过 node → registered observer → callback 闭包链路把已销毁的 sandbox 钉在内存中。
   */
  public deferredStyleObservers: Array<MutationObserver> = [];
  /** 子应用head元素 */
  public head: HTMLHeadElement;
  /** 子应用body元素 */
  public body: HTMLBodyElement;
  /** 子应用dom监听事件留存，当降级时用于保存元素事件 */
  public elementEventCacheMap: WeakMap<
    Node,
    Array<{ type: string; handler: EventListenerOrEventListenerObject; options: any }>
  > = new WeakMap();
  /** 子应用window监听事件 */
  public iframeAddEventListeners?: Array<string>;
  /** 子应用iframe on事件 */
  public iframeOnEvents?: Array<string>;
  /** 销毁链路清理跟踪器：记录被转发到主应用 window/document 上的副作用，destroy 时统一回收 */
  public eventCleanupTracker: EventCleanupTracker = new EventCleanupTracker();

  /** $wujie对象，提供给子应用的接口 */
  public provide: {
    bus: EventBus;
    shadowRoot?: ShadowRoot;
    props?: { [key: string]: any };
    location?: Object;
  };

  /** 子应用嵌套场景，父应用传递给子应用的数据 */
  public inject: {
    idToSandboxMap: Map<String, SandboxCache>;
    appEventObjMap: Map<String, EventObj>;
    mainHostPath: string;
    fontStyleSheetContainer?: HTMLElement;
  };

  /** 激活子应用
   * 1、同步路由
   * 2、动态修改iframe的fetch
   * 3、准备shadow
   * 4、准备子应用注入
   */
  public async active(options: {
    url: string;
    sync?: boolean;
    prefix?: { [key: string]: string };
    template?: string;
    el?: string | HTMLElement;
    props?: { [key: string]: any };
    alive?: boolean;
    fetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
    replace?: (code: string) => string;
  }): Promise<void> {
    const { sync, url, el, template, props, alive, prefix, fetch, replace } = options;
    this.url = url;
    this.sync = sync;
    this.alive = alive;
    this.hrefFlag = false;
    this.prefix = prefix ?? this.prefix;
    this.replace = replace ?? this.replace;
    this.provide.props = props ?? this.provide.props;
    this.activeFlag = true;
    // wait iframe init
    await this.iframeReady;

    // 处理子应用自定义fetch
    // TODO fetch检验合法性
    const iframeWindow = this.iframe.contentWindow;
    const iframeFetch = fetch
      ? (input: RequestInfo, init?: RequestInit) =>
          fetch(typeof input === "string" ? getAbsolutePath(input, (this.proxyLocation as Location).href) : input, init)
      : this.fetch;
    if (iframeFetch) {
      iframeWindow.fetch = iframeFetch;
      this.fetch = iframeFetch;
    }

    // 处理子应用路由同步
    if (this.execFlag && this.alive) {
      // 当保活模式下子应用重新激活时，只需要将子应用路径同步回主应用
      syncUrlToWindow(iframeWindow);
    } else {
      // 先将url同步回iframe，然后再同步回浏览器url
      syncUrlToIframe(iframeWindow);
      syncUrlToWindow(iframeWindow);
    }

    // inject template
    this.template = template ?? this.template;

    /* 降级处理 */
    if (this.degrade) {
      const iframeBody = rawDocumentQuerySelector.call(iframeWindow.document, "body") as HTMLElement;
      const { iframe, container } = initRenderIframeAndContainer(this.id, el ?? iframeBody, this.degradeAttrs);
      this.el = container;
      // 销毁js运行iframe容器内部dom
      if (el) clearChild(iframeBody);
      // 修复vue的event.timeStamp问题
      patchEventTimeStamp(iframe.contentWindow, iframeWindow);
      // 当销毁iframe时主动unmount子应用
      iframe.contentWindow.onunload = () => {
        this.unmount();
      };
      if (this.document) {
        if (this.alive) {
          iframe.contentDocument.replaceChild(this.document.documentElement, iframe.contentDocument.documentElement);
          // 保活场景需要事件全部恢复
          recoverEventListeners(iframe.contentDocument.documentElement, iframeWindow);
        } else {
          await renderTemplateToIframe(iframe.contentDocument, this.iframe.contentWindow, this.template);
          // 非保活场景需要恢复根节点的事件，防止react16监听事件丢失
          recoverDocumentListeners(this.document.documentElement, iframe.contentDocument.documentElement, iframeWindow);
        }
      } else {
        await renderTemplateToIframe(iframe.contentDocument, this.iframe.contentWindow, this.template);
      }
      this.document = iframe.contentDocument;
      const renderWindow = this.document.defaultView;
      if (renderWindow) {
        patchDegradeInstanceofAcrossRealms(iframeWindow, renderWindow);
      }
      return;
    }

    if (this.shadowRoot) {
      /*
       document.addEventListener was transfer to shadowRoot.addEventListener
       react 16 SyntheticEvent will remember document event for avoid repeat listen
       shadowRoot have to dispatchEvent for react 16 so can't be destroyed
       this may lead memory leak risk
       */
      this.el = renderElementToContainer(this.shadowRoot.host, el);
      if (this.alive) return;
    } else {
      // 预执行无容器，暂时插入iframe内部触发Web Component的connect
      const iframeBody = rawDocumentQuerySelector.call(iframeWindow.document, "body") as HTMLElement;
      this.el = renderElementToContainer(createWujieWebComponent(this.id), el ?? iframeBody);
    }

    await renderTemplateToShadowRoot(this.shadowRoot, iframeWindow, this.template);
    this.patchCssRules();

    // inject shadowRoot to app
    this.provide.shadowRoot = this.shadowRoot;
  }

  // 未销毁，空闲时才回调
  public requestIdleCallback(callback) {
    return requestIdleCallback(() => {
      // 假如已经被销毁了
      if (!this.iframe) return;
      callback.apply(this);
    });
  }
  /** 启动子应用
   * 1、运行js
   * 2、处理兼容样式
   */
  public async start(getExternalScripts: () => ScriptResultList): Promise<void> {
    this.execFlag = true;
    // 执行脚本
    const scriptResultList = await getExternalScripts();
    // 假如已经被销毁了
    if (!this.iframe) return;
    const iframeWindow = this.iframe.contentWindow;
    // 标志位，执行代码前设置
    iframeWindow.__POWERED_BY_WUJIE__ = true;
    // 用户自定义代码前
    const beforeScriptResultList: ScriptObjectLoader[] = getPresetLoaders("jsBeforeLoaders", this.plugins);
    // 用户自定义代码后
    const afterScriptResultList: ScriptObjectLoader[] = getPresetLoaders("jsAfterLoaders", this.plugins);
    // 同步代码
    const syncScriptResultList: ScriptResultList = [];
    // async代码无需保证顺序，所以不用放入执行队列
    const asyncScriptResultList: ScriptResultList = [];
    // defer代码需要保证顺序并且DOMContentLoaded前完成，这里统一放置同步脚本后执行
    const deferScriptResultList: ScriptResultList = [];
    scriptResultList.forEach((scriptResult) => {
      if (scriptResult.defer) deferScriptResultList.push(scriptResult);
      else if (scriptResult.async) asyncScriptResultList.push(scriptResult);
      else syncScriptResultList.push(scriptResult);
    });

    // 插入代码前
    beforeScriptResultList.forEach((beforeScriptResult) => {
      this.execQueue.push(() =>
        this.fiber
          ? this.requestIdleCallback(() => insertScriptToIframe(beforeScriptResult, iframeWindow))
          : insertScriptToIframe(beforeScriptResult, iframeWindow)
      );
    });

    // 同步代码
    syncScriptResultList.concat(deferScriptResultList).forEach((scriptResult) => {
      this.execQueue.push(() =>
        scriptResult.contentPromise.then((content) =>
          this.fiber
            ? this.requestIdleCallback(() => insertScriptToIframe({ ...scriptResult, content }, iframeWindow))
            : insertScriptToIframe({ ...scriptResult, content }, iframeWindow)
        )
      );
    });

    // 异步代码
    asyncScriptResultList.forEach((scriptResult) => {
      scriptResult.contentPromise.then((content) => {
        this.fiber
          ? this.requestIdleCallback(() => insertScriptToIframe({ ...scriptResult, content }, iframeWindow))
          : insertScriptToIframe({ ...scriptResult, content }, iframeWindow);
      });
    });

    //框架主动调用mount方法
    this.execQueue.push(this.fiber ? () => this.requestIdleCallback(() => this.mount()) : () => this.mount());

    //触发 DOMContentLoaded 事件
    const domContentLoadedTrigger = () => {
      eventTrigger(iframeWindow.document, "DOMContentLoaded");
      eventTrigger(iframeWindow, "DOMContentLoaded");
      this.execQueue.shift()?.();
    };
    this.execQueue.push(this.fiber ? () => this.requestIdleCallback(domContentLoadedTrigger) : domContentLoadedTrigger);

    // 插入代码后
    afterScriptResultList.forEach((afterScriptResult) => {
      this.execQueue.push(() =>
        this.fiber
          ? this.requestIdleCallback(() => insertScriptToIframe(afterScriptResult, iframeWindow))
          : insertScriptToIframe(afterScriptResult, iframeWindow)
      );
    });

    //触发 loaded 事件
    const domLoadedTrigger = () => {
      eventTrigger(iframeWindow.document, "readystatechange");
      eventTrigger(iframeWindow, "load");
      this.execQueue.shift()?.();
    };
    this.execQueue.push(this.fiber ? () => this.requestIdleCallback(domLoadedTrigger) : domLoadedTrigger);
    // 由于没有办法准确定位是哪个代码做了mount，保活、重建模式提前关闭loading
    if (this.alive || !isFunction(this.iframe.contentWindow.__WUJIE_UNMOUNT)) removeLoading(this.el);
    this.execQueue.shift()();

    // 所有的execQueue队列执行完毕，start才算结束，保证串行的执行子应用
    return new Promise((resolve) => {
      this.execQueue.push(() => {
        resolve();
        this.execQueue.shift()?.();
      });
    });
  }

  /**
   * 框架主动发起mount，如果子应用是异步渲染实例，比如将生命周__WUJIE_MOUNT放到async函数内
   * 此时如果采用fiber模式渲染（主应用调用mount的时机也是异步不确定的），框架调用mount时可能
   * 子应用的__WUJIE_MOUNT还没有挂载到window，所以这里封装一个mount函数，当子应用是异步渲染
   * 实例时，子应用异步函数里面最后加上window.__WUJIE.mount()来主动调用
   */
  public mount(): void {
    if (this.mountFlag) return;
    if (isFunction(this.iframe.contentWindow.__WUJIE_MOUNT)) {
      removeLoading(this.el);
      this.lifecycles?.beforeMount?.(this.iframe.contentWindow);
      this.iframe.contentWindow.__WUJIE_MOUNT();
      this.lifecycles?.afterMount?.(this.iframe.contentWindow);
      this.mountFlag = true;
    }
    if (this.alive) {
      this.lifecycles?.activated?.(this.iframe.contentWindow);
    }
    this.execQueue.shift()?.();
  }

  /** 保活模式和使用proxyLocation.href跳转链接都不应该销毁shadow */
  public async unmount(): Promise<void> {
    // 子应用卸载时 disconnect 等待 href 的 MutationObserver，避免闭包在 remount 前仍持有上下文
    this.clearDeferredStyleObservers();
    this.activeFlag = false;
    // 清理子应用过期的同步参数
    clearInactiveAppUrl();
    if (this.alive) {
      this.lifecycles?.deactivated?.(this.iframe.contentWindow);
    }
    if (!this.mountFlag) return;
    if (isFunction(this.iframe.contentWindow.__WUJIE_UNMOUNT) && !this.alive && !this.hrefFlag) {
      this.lifecycles?.beforeUnmount?.(this.iframe.contentWindow);
      await this.iframe.contentWindow.__WUJIE_UNMOUNT();
      this.lifecycles?.afterUnmount?.(this.iframe.contentWindow);
      this.mountFlag = false;
      this.bus?.$clear();
      if (!this.degrade) {
        clearChild(this.shadowRoot);
        // head body需要复用，每次都要清空事件
        removeEventListener(this.head);
        removeEventListener(this.body);
      }
      clearChild(this.head);
      clearChild(this.body);
      // styleSheetElements / dynamicScriptElements 不能在 unmount 中清空：
      // 子应用的 JS 模块只在 sandbox.start() 阶段执行一次，unmount → active 后
      // 模块代码不会重跑，再次 mount 依赖 rebuildStyleSheets() 把数组里登记的
      // 样式节点重新挂回 shadowRoot.head。两数组的彻底清理放在 destroy() 中。
    }
  }

  /** 销毁子应用 */
  public async destroy() {
    // 防重入：极端时序下 destroyApp / disconnectedCallback / startApp 可能并发触发，
    // 标志位确保同一实例只执行一次完整销毁。
    if (this.destroyed) return;
    this.destroyed = true;
    // 同步从全局 map 移除自身：确保 await 挂起期间并发的 disconnectedCallback /
    // startApp 通过 getWujieById 拿到 null，避免对同一实例重复 destroy（竞态根因）。
    deleteWujieById(this.id);
    await this.unmount();
    // 释放动态样式 / 脚本节点（unmount 阶段保留以便 rebuildStyleSheets 复用，destroy 阶段才彻底清）
    this.clearStyleSheets();
    this.clearDynamicScripts();
    this.clearFontStyleSheets();
    // 解绑等待 href 赋值的 MutationObserver，避免闭包钉住已销毁的 sandbox
    this.clearDeferredStyleObservers();
    // 先 $destroy 再置 null：清空事件并从全局 appEventObjMap 中移除当前 id 的 entry，
    // 避免 setupApp → destroyApp 反复后 map 条目持续累积。
    this.bus.$destroy();
    this.shadowRoot = null;
    this.proxy = null;
    this.proxyDocument = null;
    this.proxyLocation = null;
    this.execQueue = null;
    this.provide = null;
    this.degradeAttrs = null;
    this.styleSheetElements = null;
    this.fontStyleSheetElements = null;
    this.dynamicScriptElements = null;
    this.deferredStyleObservers = null;
    this.bus = null;
    this.replace = null;
    this.fetch = null;
    this.execFlag = null;
    this.mountFlag = null;
    this.hrefFlag = null;
    this.document = null;
    this.head = null;
    this.body = null;
    this.elementEventCacheMap = null;
    this.lifecycles = null;
    this.plugins = null;
    this.provide = null;
    this.inject = null;
    this.execQueue = null;
    this.prefix = null;
    this.iframeAddEventListeners = null;
    this.iframeOnEvents = null;
    // 清除 dom
    if (this.el) {
      clearChild(this.el);
      this.el = null;
    }
    // 清除 iframe 沙箱
    if (this.iframe) {
      const iframeWindow = this.iframe.contentWindow;
      if (iframeWindow?.__WUJIE_EVENTLISTENER__) {
        iframeWindow.__WUJIE_EVENTLISTENER__.forEach((o) => {
          iframeWindow.removeEventListener(o.type, o.listener, o.options);
        });
      }
      // patchElementEffect 给散落到主应用 DOM 上的 element 留了 baseURI / ownerDocument
      // getter，它们通过 iframeWindow.__WUJIE 动态读取。这里主动断链让残留 getter 立即
      // 降级到主 document，避免 element 把 sandbox 钉在内存中。
      // __WUJIE / $wujie 均挂在 iframeWindow 上，destroy 时须一并置 null。
      if (iframeWindow) {
        try {
          iframeWindow.__WUJIE = null;
          iframeWindow.$wujie = null;
        } catch (_) {
          /* noop: iframe 已 detach 时赋值可能抛错 */
        }
      }
      this.iframe.parentNode?.removeChild(this.iframe);
      this.iframe = null;
    }
    // 释放 window / document / location 代理：解除代理与 handler 的关联，使捕获了 iframe /
    // urlElement 的 handler 闭包不可达，斩断「主应用 → 代理闭包 → iframe」的引用链。
    try {
      this.proxyRevoke?.();
    } catch (_) {
      /* noop: 代理已释放时重复调用可能抛错 */
    }
    this.proxyRevoke = null;
    // 反向解绑 patchDocumentEffect / patchWindowEffect 在主 window / document 上挂的副作用
    this.eventCleanupTracker.cleanupAll();
  }

  /**
   * destroy 阶段清空 styleSheetElements，同时把节点从父节点 detach。
   *
   * 仅供 destroy 调用：unmount 阶段需要保留数组以便 rebuildStyleSheets 复用样式节点
   * （子应用 JS 模块只 init 一次，模块代码不会再次生成动态样式）。
   */
  public clearStyleSheets(): void {
    if (!Array.isArray(this.styleSheetElements)) return;
    this.styleSheetElements.forEach((el) => {
      try {
        el.parentNode?.removeChild(el);
      } catch (_) {
        /* noop: destroy 阶段任何异常不应中断后续清理 */
      }
    });
    this.styleSheetElements.length = 0;
  }

  /**
   * destroy 阶段清空 dynamicScriptElements，同时把残留的 <script> 节点从父节点 detach。
   * 仅供 destroy 调用，理由同 clearStyleSheets。
   */
  public clearDynamicScripts(): void {
    if (!Array.isArray(this.dynamicScriptElements)) return;
    this.dynamicScriptElements.forEach((script) => {
      try {
        script.parentNode?.removeChild(script);
      } catch (_) {
        /* noop */
      }
    });
    this.dynamicScriptElements.length = 0;
  }

  /**
   * destroy 阶段清空 fontStyleSheetElements，同时把节点从父节点 detach。
   * 使用 WUJIE_APP_ID 标识属于当前子应用的 font 样式。
   */
  public clearFontStyleSheets(): void {
    if (!Array.isArray(this.fontStyleSheetElements)) return;
    this.fontStyleSheetElements.forEach((el) => {
      try {
        el.parentNode?.removeChild(el);
      } catch (_) {
        /* noop */
      }
    });
    this.fontStyleSheetElements.length = 0;
  }

  /**
   * unmount / destroy 阶段统一 disconnect 等待 href 赋值的 MutationObserver。
   * observer 在 href 命中或超时兜底时会自行 disconnect 并出队；
   * 这里兜底处理「子应用先于 href 赋值被卸载/销毁」的场景。
   */
  public clearDeferredStyleObservers(): void {
    if (!Array.isArray(this.deferredStyleObservers)) return;
    this.deferredStyleObservers.forEach((observer) => {
      try {
        observer.disconnect();
      } catch (_) {
        /* noop: destroy 阶段任何异常不应中断后续清理 */
      }
    });
    this.deferredStyleObservers.length = 0;
  }

  /**
   * 创建或获取 font 样式容器（挂载在最外层 document.head）
   * 用于存放子应用的 @font-face 样式，确保嵌套子应用也能正确应用字体
   */
  private createFontStyleSheetContainer(): HTMLElement {
    const container = rawDocumentQuerySelector.call(document, `[${WUJIE_FONT_STYLE_CONTAINER_ATTR}]`);
    if (container) return container as HTMLElement;

    const styleElement = document.createElement("style");
    styleElement.setAttribute(WUJIE_FONT_STYLE_CONTAINER_ATTR, "");
    document.head.appendChild(styleElement);
    return styleElement;
  }

  /** 当子应用再次激活后，只运行mount函数，样式需要重新恢复 */
  public rebuildStyleSheets(): void {
    if (this.styleSheetElements && this.styleSheetElements.length) {
      this.styleSheetElements.forEach((styleSheetElement) => {
        rawElementAppendChild.call(this.degrade ? this.document.head : this.shadowRoot.head, styleSheetElement);
      });
    }
    this.patchCssRules();
  }

  /**
   * 子应用样式打补丁
   * 1、兼容:root选择器样式到:host选择器上
   * 2、将@font-face定义到shadowRoot外部
   */
  public patchCssRules(): void {
    if (this.degrade) return;
    if (this.shadowRoot.host.hasAttribute(WUJIE_DATA_ATTACH_CSS_FLAG)) return;
    const [hostStyleSheetElement, fontStyleSheetElement] = getPatchStyleElements(
      Array.from(this.iframe.contentDocument.querySelectorAll("style")).map(
        (styleSheetElement) => styleSheetElement.sheet
      )
    );
    if (hostStyleSheetElement) {
      this.shadowRoot.head.appendChild(hostStyleSheetElement);
      this.styleSheetElements.push(hostStyleSheetElement);
    }
    if (fontStyleSheetElement) {
      this.inject.fontStyleSheetContainer?.appendChild(fontStyleSheetElement);
      fontStyleSheetElement.setAttribute(WUJIE_APP_ID, this.id);
      this.fontStyleSheetElements.push(fontStyleSheetElement);
    }
    (hostStyleSheetElement || fontStyleSheetElement) &&
      this.shadowRoot.host.setAttribute(WUJIE_DATA_ATTACH_CSS_FLAG, "");
  }

  /**
   * @param id 子应用的id，唯一标识
   * @param url 子应用的url，可以包含protocol、host、path、query、hash
   */
  constructor(options: {
    name: string;
    url: string;
    attrs: { [key: string]: any };
    degradeAttrs: { [key: string]: any };
    fiber: boolean;
    degrade;
    plugins: Array<plugin>;
    lifecycles: lifecycles;
    iframeAddEventListeners?: Array<string>;
    iframeOnEvents?: Array<string>;
  }) {
    // 传递inject给嵌套子应用（显式 as：__WUJIE_INJECT 全局类型是 Partial，需断言回完整结构）
    if (window.__POWERED_BY_WUJIE__) this.inject = window.__WUJIE.inject as Wujie["inject"];
    else {
      this.inject = {
        idToSandboxMap: idToSandboxCacheMap,
        appEventObjMap,
        mainHostPath: window.location.protocol + "//" + window.location.host,
        fontStyleSheetContainer: this.createFontStyleSheetContainer(),
      };
    }
    const { name, url, attrs, fiber, degradeAttrs, degrade, lifecycles, plugins } = options;
    this.id = name;
    this.fiber = fiber;
    this.degrade = degrade || !wujieSupport;
    this.bus = new EventBus(this.id);
    this.url = url;
    this.degradeAttrs = degradeAttrs;
    this.provide = { bus: this.bus };
    this.styleSheetElements = [];
    this.execQueue = [];
    this.lifecycles = lifecycles;
    this.plugins = getPlugins(plugins);
    this.iframeAddEventListeners = options.iframeAddEventListeners;
    this.iframeOnEvents = options.iframeOnEvents;

    // 创建目标地址的解析
    const { urlElement, appHostPath, appRoutePath } = appRouteParse(url);
    const { mainHostPath } = this.inject;
    // 创建iframe
    this.iframe = iframeGenerator(this, attrs, mainHostPath, appHostPath, appRoutePath);

    if (this.degrade) {
      const { proxyDocument, proxyLocation, proxyRevoke } = localGenerator(
        this.iframe,
        urlElement,
        mainHostPath,
        appHostPath
      );
      this.proxyDocument = proxyDocument;
      this.proxyLocation = proxyLocation;
      this.proxyRevoke = proxyRevoke;
    } else {
      const { proxyWindow, proxyDocument, proxyLocation, proxyRevoke } = proxyGenerator(
        this.iframe,
        urlElement,
        mainHostPath,
        appHostPath
      );
      this.proxy = proxyWindow;
      this.proxyDocument = proxyDocument;
      this.proxyLocation = proxyLocation;
      this.proxyRevoke = proxyRevoke;
    }
    this.provide.location = this.proxyLocation;

    addSandboxCacheWithWujie(this.id, this);
  }
}
